"use client";

import { useEffect, useRef } from "react";
import { Status } from "@/components/ui/Status";
import { products } from "@/content/facts";
import { marketDemo as T } from "@/content/market";
import { ASUNCION_TZ } from "@/lib/asuncion";
import { worldBus } from "@/lib/world-bus";
import { clamp, smoothstep } from "@/world/core/math";
import styles from "./MarketOverlay.module.css";
import { quadMatrix } from "./quad";

// Lo que se dibuja encima del súper 3D, alineado con la escena (inicio, plano 6, y
// Productos). El mundo escribe en cada cuadro dónde cae cada cosa en pantalla
// (worldBus.marketScreen); acá solo se acomoda el DOM, sin pasar por React:
//
// - La etiqueta de la yerba consultada, sobre la góndola.
// - La conversación del celular de la caja (Yvyra): un bloque HTML de 300 × 640 px
//   llevado a las cuatro esquinas de la pantalla 3D con una matriz proyectiva. Al final
//   entra el aviso de Tajy y la pantalla crece hasta cubrir el cuadro (el corte del
//   inicio pasa a la cámara del techo).
// - La vista de Tajy: cajas y esqueletos sobre las personas, los faltantes de la
//   góndola, la alerta de caída que espera confirmación y el estado "en desarrollo".
//
// Es decorativo (aria-hidden): lo que dice está en los textos de la página.

const PW = 300;
const PH = 640;
const MAX_PEOPLE = 6;
const MAX_GAPS = 10;

/** Hora de Asunción con segundos, como la escribe una cámara. */
const clockFmt = new Intl.DateTimeFormat("es-PY", {
  timeZone: ASUNCION_TZ,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** Esquinas de una caja como cuatro escuadras (el trazo de un detector). */
function brackets(x0: number, y0: number, x1: number, y1: number) {
  const l = Math.max(5, Math.min(16, (x1 - x0) * 0.28, (y1 - y0) * 0.2));
  return (
    `M${x0} ${y0 + l}V${y0}H${x0 + l}` +
    `M${x1 - l} ${y0}H${x1}V${y0 + l}` +
    `M${x1} ${y1 - l}V${y1}H${x1 - l}` +
    `M${x0 + l} ${y1}H${x0}V${y1 - l}`
  );
}

const f1 = (v: number) => v.toFixed(1);

/**
 * `visibility`: opacidad de la capa (la lee en cada cuadro). Si la página ocupa la parte
 * de abajo de la pantalla, lo declara con la variable CSS --hud-bottom en el contenedor
 * y el monitor de Tajy se corre por encima.
 */
export function MarketOverlay({ visibility }: { visibility?: () => number }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const visRef = useRef(visibility);
  useEffect(() => {
    visRef.current = visibility;
  }, [visibility]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const $ = <E extends Element = HTMLElement>(sel: string) => root.querySelector<E>(sel)!;
    const tag = $("[data-tag]");
    const phone = $("[data-phone]");
    const msgs = [...root.querySelectorAll<HTMLElement>("[data-msg]")];
    const sub = $("[data-sub]");
    const handoff = $("[data-handoff]");
    const hud = $("[data-hud]");
    const cover = $("[data-cover]");
    const clock = $("[data-clock]");
    const count = $("[data-count]");
    const alert = $("[data-alert]");
    const monitor = $("[data-monitor]");
    const persons = [...root.querySelectorAll<SVGGElement>("[data-person]")].map((g) => ({
      g,
      box: g.querySelector<SVGPathElement>("[data-box]")!,
      bones: g.querySelector<SVGPathElement>("[data-bones]")!,
      label: g.querySelector<SVGTextElement>("text")!,
      chip: g.querySelector<SVGRectElement>("rect")!,
    }));
    const gaps = [...root.querySelectorAll<SVGGElement>("[data-gap]")].map((g) => ({
      g,
      rect: g.querySelector<SVGRectElement>("rect")!,
      label: g.querySelector<SVGTextElement>("text")!,
    }));
    const yvyraAt = T.chat.messages.filter((m) => m.from === "yvyra").map((m) => m.at);

    let raf = 0;
    let cctvSince = -1;
    let lastClock = "";
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const vis = worldBus.status === "ready" ? clamp(visRef.current ? visRef.current() : 1) : 0;
      root.style.opacity = vis.toFixed(3);
      if (vis < 0.01) {
        root.dataset.off = "";
        cctvSince = -1;
        return;
      }
      delete root.dataset.off;
      const m = worldBus.marketScreen;
      const W = window.innerWidth;
      const H = window.innerHeight;

      // ---- El celular ----
      const p = m.phone;
      const phoneH = p ? Math.hypot(p[6] - p[0], p[7] - p[1]) : 0;
      const phoneA = p ? smoothstep(36, 90, phoneH) : 0;
      if (p && phoneA > 0) {
        // Al final la pantalla crece hasta el cuadro entero (el corte a Tajy).
        const grow = smoothstep(0.93, 1, m.chat);
        const full = [0, 0, W, 0, W, H, 0, H];
        const q = grow > 0 ? p.map((v, i) => v + (full[i] - v) * grow) : p;
        phone.style.transform = quadMatrix(PW, PH, q);
        phone.style.opacity = phoneA.toFixed(3);
        phone.style.setProperty("--feed", smoothstep(0.9, 0.97, m.chat).toFixed(3));
        phone.style.setProperty("--grow", grow.toFixed(3));
        phone.toggleAttribute("data-notice", m.chat > 0.86);
        msgs.forEach((el, i) => el.toggleAttribute("data-on", m.chat >= T.chat.messages[i].at));
        const typing = yvyraAt.some((a) => m.chat > a - 0.13 && m.chat < a);
        sub.textContent = typing ? T.chat.typing : T.chat.by;
        sub.toggleAttribute("data-typing", typing);
        handoff.toggleAttribute("data-on", m.chat > 0.82);
        phone.hidden = false;
      } else {
        phone.hidden = true;
      }

      // ---- La yerba consultada ----
      const tagA = m.query && m.cctv < 0.5 ? m.focus * (1 - smoothstep(0.18, 0.3, phoneH / H)) : 0;
      if (m.query && tagA > 0.01) {
        tag.hidden = false;
        tag.style.opacity = tagA.toFixed(3);
        tag.style.transform = `translate(${f1(m.query.x)}px, ${f1(m.query.y)}px)`;
      } else tag.hidden = true;

      // ---- Tajy ----
      const on = m.cctv > 0.5;
      hud.hidden = !on;
      if (!on) {
        cctvSince = -1;
        return;
      }
      if (cctvSince < 0) cctvSince = now;
      // Del corte a la cámara del techo: la imagen sale del negro de la pantalla.
      cover.style.opacity = (1 - smoothstep(0, 650, now - cctvSince)).toFixed(3);
      const c = clockFmt.format(new Date());
      if (c !== lastClock) {
        lastClock = c;
        clock.textContent = c;
      }
      count.textContent = String(m.people.length);

      let fallen: [number, number, number, number] | null = null;
      persons.forEach((el, i) => {
        const pp = m.people[i];
        if (!pp || !pp.visible || pp.box[2] - pp.box[0] < 6) {
          el.g.setAttribute("visibility", "hidden");
          return;
        }
        el.g.setAttribute("visibility", "visible");
        const [x0, y0, x1, y1] = pp.box;
        el.box.setAttribute("d", brackets(x0, y0, x1, y1));
        let d = "";
        for (let k = 0; k < pp.bones.length; k += 4) d += `M${f1(pp.bones[k])} ${f1(pp.bones[k + 1])}L${f1(pp.bones[k + 2])} ${f1(pp.bones[k + 3])}`;
        el.bones.setAttribute("d", d);
        el.g.toggleAttribute("data-fallen", pp.fallen);
        const text = pp.fallen ? T.tajy.fallen : T.tajy.person;
        if (el.label.textContent !== text) el.label.textContent = text;
        el.label.setAttribute("x", f1(x0 + 5));
        el.label.setAttribute("y", f1(y0 - 7));
        el.chip.setAttribute("x", f1(x0));
        el.chip.setAttribute("y", f1(y0 - 21));
        el.chip.setAttribute("width", String(pp.fallen ? 98 : 58));
        if (pp.fallen) fallen = pp.box;
      });
      gaps.forEach((el, i) => {
        const g = m.gaps[i];
        if (!g) {
          el.g.setAttribute("visibility", "hidden");
          return;
        }
        el.g.setAttribute("visibility", "visible");
        el.rect.setAttribute("x", f1(g.x));
        el.rect.setAttribute("y", f1(g.y));
        el.rect.setAttribute("width", f1(g.w));
        el.rect.setAttribute("height", f1(g.h));
        el.label.setAttribute("x", f1(g.x + 3));
        el.label.setAttribute("y", f1(g.y - 4));
        // Solo el primero lleva el nombre: el resto se entiende por el trazo.
        el.label.setAttribute("visibility", i === 0 ? "visible" : "hidden");
      });

      // La alerta de caída, al costado de la persona (dentro del cuadro).
      if (fallen) {
        const [x0, y0, x1, y1] = fallen as [number, number, number, number];
        const cw = Math.min(300, W - 32);
        const ah = alert.offsetHeight;
        // Dentro del monitor: por encima de lo que la página tenga abajo.
        const floorY = monitor.getBoundingClientRect().bottom - 12;
        let x = x1 + 18;
        let y = y0;
        if (W < 640) {
          // Angosto: la tarjeta ocupa el ancho; va arriba de la persona (o abajo si no entra).
          x = 16;
          y = y0 - ah - 34;
          if (y < 150) y = y1 + 16;
        } else if (x + cw > W - 16) x = x0 - cw - 18;
        x = Math.max(16, Math.min(W - cw - 16, x));
        y = Math.max(96, Math.min(floorY - ah, y));
        alert.style.transform = `translate(${f1(x)}px, ${f1(y)}px)`;
        alert.style.width = `${cw}px`;
        alert.toggleAttribute("data-on", m.fall > 0.95);
      } else alert.toggleAttribute("data-on", false);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={rootRef} className={styles.overlay} aria-hidden="true" data-off="">
      {/* La yerba de la góndola: lo que el celular va a contestar. */}
      <div data-tag="" className={styles.tag} hidden>
        <span className={styles.tagDot} />
        <span className={styles.tagCard}>
          <span className={styles.tagName}>{T.query.name}</span>
          <span className={styles.tagLine}>
            <span>{T.query.stock}</span>
            <span>{T.query.price}</span>
          </span>
        </span>
      </div>

      {/* La pantalla del celular de la caja. */}
      <div data-phone="" className={styles.phone} hidden>
        <div className={styles.phoneUi}>
          <div className={styles.phoneHead}>
            <span className={styles.avatar} />
            <span className={styles.who}>
              <span className={styles.contact}>{T.chat.contact}</span>
              <span data-sub="" className={styles.sub}>
                {T.chat.by}
              </span>
            </span>
          </div>
          <div className={styles.thread}>
            {T.chat.messages.map((msg) => (
              <p key={msg.text} data-msg="" data-from={msg.from} className={styles.msg}>
                {msg.text}
                <span className={styles.time}>{msg.time}</span>
              </p>
            ))}
            <p data-handoff="" className={styles.handoff}>
              {T.chat.handoff}
            </p>
          </div>
          <p className={styles.compose}>{T.chat.example}</p>
        </div>
        <div className={styles.notice}>
          <span className={styles.noticeDot} />
          <span>
            <span className={styles.noticeTitle}>{T.notice.title}</span>
            <span className={styles.noticeBody}>{T.notice.body}</span>
          </span>
        </div>
        <div className={styles.feed} />
      </div>

      {/* La vista de Tajy. */}
      <div data-hud="" className={styles.hud} hidden>
        <svg className={styles.marks} width="100%" height="100%">
          {Array.from({ length: MAX_GAPS }, (_, i) => (
            <g key={`g${i}`} data-gap="" className={styles.gap} visibility="hidden">
              <rect />
              <text>{T.tajy.gap}</text>
            </g>
          ))}
          {Array.from({ length: MAX_PEOPLE }, (_, i) => (
            <g key={`p${i}`} data-person="" className={styles.person} visibility="hidden">
              <path data-bones="" className={styles.bones} />
              <path data-box="" className={styles.box} />
              <rect height="18" rx="3" className={styles.chip} />
              <text className={styles.label}>{T.tajy.person}</text>
            </g>
          ))}
        </svg>
        <div data-monitor="" className={styles.frame} />
        <div className={styles.top}>
          <span className={styles.rec}>
            <span className={styles.recDot} />
            {T.tajy.rec}
          </span>
          <span>{T.tajy.cam}</span>
          <span data-clock="" className={styles.clock} />
        </div>
        <div className={styles.state}>
          <span className={styles.brand}>{products.tajy.name}</span>
          <Status kind={products.tajy.status.kind}>{products.tajy.status.label}</Status>
        </div>
        <div className={styles.bottom}>
          <span>
            {T.tajy.people} <b data-count="">0</b>
          </span>
          <span>{T.tajy.local}</span>
        </div>
        <div data-alert="" className={styles.alert}>
          <p className={styles.alertTitle}>{T.tajy.alertTitle}</p>
          <p className={styles.alertBody}>{T.tajy.alertBody}</p>
          <p className={styles.alertActions}>
            <span>{T.tajy.confirm}</span>
            <span>{T.tajy.dismiss}</span>
          </p>
        </div>
        <div data-cover="" className={styles.cover} />
      </div>
    </div>
  );
}
